import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { MOSTRAR_FREQUENCIA, MODO_PADRAO } from '../utils/telegramEscopo';
import TelegramFeedbackFields from '../components/TelegramFeedbackFields';
import LoginAccessStatus from '../components/LoginAccessStatus';
import { toInitialDateTime } from '../components/DateTimeInputBR';
import './UserCreate.css';

const maskDate = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};
const brToISO = (br) => { const m = br.match(/^(\d{2})\/(\d{2})\/(\d{4})$/); return m ? `${m[3]}-${m[2]}-${m[1]}` : ''; };
const isoToBR = (iso) => { if (!iso) return ''; const p = iso.slice(0, 10).split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : ''; };

const toInitialBR = (val) => {
    if (!val) return '';
    const d = new Date(val);
    if (isNaN(d)) return '';
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const DateInputBR = ({ value, onChange, id }) => {
    const ref = useRef(null);
    return (
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <input
                id={id}
                type="text"
                value={value}
                onChange={(e) => onChange(maskDate(e.target.value))}
                className="form-input"
                placeholder="dd/mm/aaaa"
                maxLength={10}
                style={{ flex: 1 }}
            />
            <input
                ref={ref}
                type="date"
                style={{ position: 'absolute', opacity: 0, width: 0, height: 0, overflow: 'hidden' }}
                value={brToISO(value)}
                onChange={(e) => onChange(isoToBR(e.target.value))}
            />
            <button
                type="button"
                onClick={() => ref.current?.showPicker?.()}
                style={{ background: 'none', border: '1px solid #ccc', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}
                title="Abrir calendário"
            >
                📅
            </button>
        </div>
    );
};

const UserEdit = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, company, scrollY } = location.state || {};
    const backUrl = scrollY ? `/users?scrollY=${scrollY}` : '/users';

    const [fullname, setFullname] = useState(user?.fullname || '');
    const [username, setUsername] = useState(user?.username || '');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [role, setRole] = useState(user?.role || 'salesman');
    const [isVisible, setIsVisible] = useState(user?.is_visible !== undefined ? user.is_visible : true);
    const [isActive, setIsActive] = useState(user?.is_active !== undefined ? user.is_active : true);
    const [recordingsStart, setRecordingsStart] = useState(toInitialBR(user?.recordings_start));
    const [appVersionName, setAppVersionName] = useState(user?.app_version_name || 'inherit');
    const [feedbackReadEnabled, setFeedbackReadEnabled] = useState(user?.feedback_read_enabled || false);
    // WhatsApp: o valor gravado é o wa_id (só dígitos). Mostramos como veio — quem
    // reformata para exibição arrisca mandar de volta um formato que a API rejeita.
    const [whatsappPhone, setWhatsappPhone] = useState(user?.whatsapp_phone || '');
    const [whatsappEnabled, setWhatsappEnabled] = useState(user?.whatsapp_enabled || false);
    // Telegram
    const [telegramUsername, setTelegramUsername] = useState(user?.telegram_username || '');
    const [feedbackTelegramEnabled, setFeedbackTelegramEnabled] = useState(user?.feedback_telegram_enabled || false);
    const [feedbackTelegramMode, setFeedbackTelegramMode] = useState(user?.feedback_telegram_mode || MODO_PADRAO);
    const [feedbackTelegramShort, setFeedbackTelegramShort] = useState(
        user?.feedback_telegram_short !== undefined ? user.feedback_telegram_short : true);
    const [feedbackTelegramLong, setFeedbackTelegramLong] = useState(
        user?.feedback_telegram_long || false);
    const [feedbackReadStartDate, setFeedbackReadStartDate] = useState(toInitialBR(user?.feedback_read_enabled_start_date));
    // onboarding_completed_at: somente-leitura; a única ação permitida é limpar (zerar)
    const onboardingAt = toInitialDateTime(user?.onboarding_completed_at);
    const [onboardingCleared, setOnboardingCleared] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const roles = [
        { value: 'admin', label: 'Administrador' },
        { value: 'salesman', label: 'Vendedor' },
        { value: 'manager', label: 'Gerente' },
        { value: 'supervisor', label: 'Supervisor' },
        // 'director' existia no banco (32 em produção) e não nesta lista: abrir a edição de um
        // diretor deixava o select vazio e salvar trocava o papel dele sem ninguém pedir.
        { value: 'director', label: 'Diretor' },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });

        if (!fullname.trim() || !username.trim()) {
            setMessage({ type: 'error', text: 'Nome completo e usuário são obrigatórios.' });
            return;
        }

        if (password && password !== confirmPassword) {
            setMessage({ type: 'error', text: 'As senhas não coincidem.' });
            return;
        }

        if (password && password.length < 4) {
            setMessage({ type: 'error', text: 'A senha deve ter pelo menos 4 caracteres.' });
            return;
        }

        try {
            setSubmitting(true);

            const payload = {
                salesman_id: user.id,
                company_id: company?.id,
                fullname: fullname.trim(),
                username: username.trim(),
                role,
                is_visible: isVisible,
                is_active: isActive,
            };

            if (password) {
                payload.password = password;
            }
            if (role === 'salesman' && recordingsStart) {
                const iso = brToISO(recordingsStart);
                if (iso) payload.recordings_start = iso;
            }
            // app_version_name só p/ salesman ('inherit' => NULL, herda da loja)
            if (role === 'salesman') {
                payload.app_version_name = appVersionName;
            }
            payload.feedback_read_enabled = feedbackReadEnabled;
            if (feedbackReadEnabled && feedbackReadStartDate) {
                const iso = brToISO(feedbackReadStartDate);
                if (iso) payload.feedback_read_enabled_start_date = iso;
            } else {
                payload.feedback_read_enabled_start_date = null;
            }
            // '' vira null de propósito: é assim que a tela apaga um telefone errado.
            payload.whatsapp_phone = whatsappPhone.trim() || '';
            // Desligar nao apaga whatsapp_opt_in_at: o primeiro sim fica registrado.
            payload.whatsapp_enabled = whatsappEnabled && !!whatsappPhone.trim();
            payload.telegram_username = telegramUsername.trim() || null;
            payload.feedback_telegram_enabled = feedbackTelegramEnabled;
            // Com o campo escondido, a tela não deixa ninguém em 'digest' sem querer: 'digest'
            // hoje é entrega ZERO, porque o agendador do resumo ainda não existe.
            payload.feedback_telegram_mode = MOSTRAR_FREQUENCIA ? feedbackTelegramMode : MODO_PADRAO;
            payload.feedback_telegram_short = feedbackTelegramShort;
            payload.feedback_telegram_long = feedbackTelegramLong;
            // onboarding_completed_at só pode ser ZERADO (não é editável)
            if (onboardingCleared && onboardingAt) {
                payload.clear_onboarding = true;
            }

            const result = await apiService.updateUser(payload);

            if (result.success) {
                setMessage({ type: 'success', text: `Usuário "${fullname.trim()}" atualizado com sucesso!` });
                setPassword('');
                setConfirmPassword('');
            } else {
                setMessage({ type: 'error', text: result.message || 'Erro ao atualizar usuário.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Erro ao atualizar usuário. Tente novamente.' });
        } finally {
            setSubmitting(false);
        }
    };

    if (!user) {
        return (
            <div className="user-create">
                <div className="user-create-header">
                    <button className="btn-back" onClick={() => navigate(backUrl)}>
                        &#8592; Voltar
                    </button>
                    <h1>Editar Usuário</h1>
                </div>
                <div className="alert alert-error">
                    Usuário não encontrado. Volte e tente novamente.
                </div>
            </div>
        );
    }

    return (
        <div className="user-create">
            <div className="user-create-header">
                <button className="btn-back" onClick={() => navigate(backUrl)}>
                    &#8592; Voltar
                </button>
                <h1>Editar Usuário</h1>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    {message.text}
                </div>
            )}

            <form className="user-create-form" onSubmit={handleSubmit}>
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="fullname">Nome Completo</label>
                        <input
                            id="fullname"
                            type="text"
                            value={fullname}
                            onChange={(e) => setFullname(e.target.value)}
                            className="form-input"
                            placeholder="Digite o nome completo"
                            autoFocus
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="username">Usuário (login)</label>
                        <input
                            id="username"
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="form-input"
                            placeholder="Digite o nome de usuário"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="role">Perfil</label>
                        <select
                            id="role"
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            className="form-input"
                        >
                            {roles.map((r) => (
                                <option key={r.value} value={r.value}>
                                    {r.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    {/* .form-row é flex e .form-group é flex:1 — com um filho só o select
                        esticava a linha inteira. Espaçador vazio, mesmo recurso que o
                        UserCreate já usava nesta mesma linha. */}
                    <div className="form-group" />
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="isVisible">Visível</label>
                        <select
                            id="isVisible"
                            value={isVisible ? 'true' : 'false'}
                            onChange={(e) => setIsVisible(e.target.value === 'true')}
                            className="form-input"
                        >
                            <option value="true">Sim</option>
                            <option value="false">Não</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label htmlFor="isActive">Ativo</label>
                        <select
                            id="isActive"
                            value={isActive ? 'true' : 'false'}
                            onChange={(e) => setIsActive(e.target.value === 'true')}
                            className="form-input"
                        >
                            <option value="true">Sim</option>
                            <option value="false">Não</option>
                        </select>
                    </div>
                </div>

                {role === 'salesman' && (
                  <>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="recordingsStart">Data de Início</label>
                            <DateInputBR
                                id="recordingsStart"
                                value={recordingsStart}
                                onChange={setRecordingsStart}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="appVersionName">Versão do app</label>
                            <select
                                id="appVersionName"
                                value={appVersionName}
                                onChange={(e) => setAppVersionName(e.target.value)}
                                className="form-input"
                            >
                                <option value="inherit">Herdar da loja (padrão)</option>
                                <option value="default">default</option>
                                <option value="gamification">gamification</option>
                            </select>
                            <span className="field-hint">
                                Se escolher uma versão, apenas este vendedor será alterado. "Herdar da loja" volta a usar a versão definida na loja.
                            </span>
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Onboarding concluído em</label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={onboardingCleared ? '' : (onboardingAt || '')}
                                    readOnly
                                    disabled
                                    placeholder="—"
                                    style={{ flex: 1 }}
                                />
                                {onboardingAt && !onboardingCleared && (
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setOnboardingCleared(true)}
                                        title="Zerar a data de onboarding"
                                    >
                                        Limpar
                                    </button>
                                )}
                            </div>
                            <span className="field-hint">
                                {onboardingCleared
                                    ? 'Será zerado ao salvar (onboarding marcado como não concluído).'
                                    : 'Somente leitura. A única ação permitida é limpar (zerar) a data.'}
                            </span>
                        </div>
                        <div className="form-group" />
                    </div>
                  </>
                )}

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="feedbackReadEnabled">Receber Feedbacks?</label>
                        <select
                            id="feedbackReadEnabled"
                            value={feedbackReadEnabled ? 'true' : 'false'}
                            onChange={(e) => {
                                const enabled = e.target.value === 'true';
                                setFeedbackReadEnabled(enabled);
                                if (!enabled) setFeedbackReadStartDate('');
                            }}
                            className="form-input"
                        >
                            <option value="false">Não</option>
                            <option value="true">Sim</option>
                        </select>
                    </div>
                    {feedbackReadEnabled ? (
                        <div className="form-group">
                            <label htmlFor="feedbackReadStartDate">Data Início Feedbacks <span className="field-optional">(opcional)</span></label>
                            <DateInputBR
                                id="feedbackReadStartDate"
                                value={feedbackReadStartDate}
                                onChange={setFeedbackReadStartDate}
                            />
                        </div>
                    ) : (
                        <div className="form-group" />
                    )}
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="whatsappPhone">WhatsApp <span className="field-optional">(opcional)</span></label>
                        <input
                            id="whatsappPhone"
                            type="tel"
                            value={whatsappPhone}
                            onChange={(e) => setWhatsappPhone(e.target.value)}
                            className="form-input"
                            placeholder="(19) 98113-1421"
                        />
                        <small className="field-hint">
                            Com DDD. Pode digitar com parênteses e traço — o sistema arruma.
                            Apagar o campo remove o número.
                        </small>
                    </div>
                    <div className="form-group">
                        <label htmlFor="whatsappEnabled">Receber relatórios por WhatsApp?</label>
                        <select
                            id="whatsappEnabled"
                            value={whatsappEnabled ? 'true' : 'false'}
                            onChange={(e) => setWhatsappEnabled(e.target.value === 'true')}
                            className="form-input"
                            disabled={!whatsappPhone.trim()}
                        >
                            <option value="false">Não</option>
                            <option value="true">Sim</option>
                        </select>
                        <small className="field-hint">
                            {whatsappPhone.trim()
                                ? 'Ao marcar Sim você registra o consentimento da pessoa. Desligar não apaga a data do primeiro sim.'
                                : 'Preencha o telefone para poder ligar o envio automático.'}
                        </small>
                    </div>
                </div>

                <TelegramFeedbackFields
                    userId={user?.user_id || user?.id}
                    role={role}
                    username={telegramUsername}
                    onUsernameChange={setTelegramUsername}
                    enabled={feedbackTelegramEnabled}
                    onEnabledChange={setFeedbackTelegramEnabled}
                    short={feedbackTelegramShort}
                    onShortChange={setFeedbackTelegramShort}
                    long={feedbackTelegramLong}
                    onLongChange={setFeedbackTelegramLong}
                    linkedInicial={user?.telegram_linked}
                    linkedAtInicial={user?.telegram_linked_at}
                />

                <div className="form-row">
                    <LoginAccessStatus user={user} />
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="password">Nova Senha (deixe em branco para manter)</label>
                        <input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="form-input"
                            placeholder="Nova senha (opcional)"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirmar Nova Senha</label>
                        <input
                            id="confirmPassword"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="form-input"
                            placeholder="Confirme a nova senha"
                        />
                    </div>
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={() => navigate(backUrl)}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-submit"
                        disabled={submitting}
                    >
                        {submitting ? 'Salvando...' : 'Salvar Alterações'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default UserEdit;
